import React from "react";
import {
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from "components/icons/solid";
import { cn } from "lib/cn";

const PaginationButton = ({
    className,
    children,
    disabled,
    type: _type,
    ...restProps
}: React.HTMLProps<HTMLButtonElement>) => {
    return (
        <button
            className={cn("p-1 text-sm", disabled && "cursor-auto text-black", className)}
            {...restProps}
        >
            {children}
        </button>
    );
};

export const TablePagination = ({
    total = 0,
    page = 0,
    onChangePage,
    pageSize = 10,
    onChangePageSize,
    message = "Rows per page",
}: {
    total?: number;
    page: number | undefined;
    onChangePage?: (page: number | undefined) => void;
    pageSize?: number | undefined;
    onChangePageSize?: (pageSize: number | undefined) => void;
    message?: string;
}) => {
    const totalPages = Math.ceil(total / pageSize);

    const calculatePaginationRanges = (pageSize: number) => {
        const startIndex = ((page ? page + 1 : 1) - 1) * pageSize + 1;
        const endIndex = Math.min(startIndex + pageSize - 1, total);

        return `${startIndex} - ${endIndex} of ${total}`;
    };

    const range = calculatePaginationRanges(pageSize);

    let goLast;
    if (totalPages > 1 && page < totalPages - 1) {
        goLast = () => {
            onChangePage?.(totalPages - 1);
        };
    }
    let goFirst;
    if (page > 0) {
        goFirst = () => onChangePage?.(undefined);
    }
    let goNext;
    if (page < totalPages - 1 || (page === totalPages - 1 && (page + 1) * pageSize < total)) {
        goNext = () => onChangePage?.(page + 1);
    }
    let goPrev;
    if (page > 0) {
        goPrev = () => {
            if (page === 1) return onChangePage?.(undefined);
            onChangePage?.(page - 1);
        };
    }

    const onRowsPerPage = (size: number) => {
        onChangePage?.(undefined);
        onChangePageSize?.(size === 10 ? undefined : size);
    };

    return (
        <div className="flex items-center justify-end gap-3">
            {pageSize && onChangePageSize && (
                <div className="flex items-center gap-3">
                    <div className="text-sm whitespace-nowrap text-slate-800">{message}</div>
                    <select
                        onChange={(e) => onRowsPerPage(parseInt(e.target.value))}
                        value={pageSize}
                        className="block h-7 w-20 rounded-full border bg-gray-50 px-2 py-1 text-sm text-slate-600 shadow-md focus:border-slate-300 focus:ring-3 focus:outline-hidden"
                    >
                        {[10, 15, 20, 25, 30].map((i) => (
                            <option key={i} value={i}>
                                {i}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className="text-sm whitespace-nowrap text-black">{range}</div>

            <div className="flex items-center gap-1 p-2">
                <PaginationButton onClick={goFirst} disabled={!goFirst}>
                    <ChevronDoubleLeftIcon className="h-4 w-4" />
                </PaginationButton>
                <PaginationButton onClick={goPrev} disabled={!goPrev}>
                    <ChevronLeftIcon className="h-4 w-4" />
                </PaginationButton>
                <PaginationButton onClick={goNext} disabled={!goNext}>
                    <ChevronRightIcon className="h-4 w-4" />
                </PaginationButton>
                <PaginationButton onClick={goLast} disabled={!goLast}>
                    <ChevronDoubleRightIcon className="h-4 w-4" />
                </PaginationButton>
            </div>
        </div>
    );
};
